import {
  Box,
  Badge,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useEffect, useRef } from "react";
import { AiOutlineMenu } from "react-icons/ai";
import { RiCloseLine, RiDashboardLine, RiLogoutBoxRLine, RiSettings4Line } from "react-icons/ri";
import { FaBook, FaChartLine } from "react-icons/fa";
import { MdHomeWork, MdQuiz, MdSchedule } from "react-icons/md";
import { HiSpeakerphone } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import StudentWrapper from "../Auth/StudentWrapper";
import ThemeToggleButton from "../dashboard/ThemeToggleButton";
import AnnouncementsCarousel from "./AnnouncementsCarousel";
import RecentContentBell from "./RecentContentBell";
import { logoutUserAction } from "../../modules/auth/Actions";
import { getActiveAnnouncementsRequest } from "../../modules/students-modules/announcements/Actions";
import { getAllExamsAndHomeworksRequest } from "../../modules/students-modules/exams/Actions";
import { getPerformanceAnalyticsRequest } from "../../modules/students-modules/performance/Actions";
import { brandPalette } from "../../constants/brandPalette";
import {
  adminFooterControlProps,
  adminFooterNameProps,
  adminFooterProps,
  adminFooterSettingsButtonProps,
  adminHeaderSubtitleProps,
  adminMobileDrawerCloseButtonProps,
  adminMobileDrawerHeaderProps,
  adminMobileDrawerShellProps,
  adminMobileDrawerTitleProps,
  adminNavItemProps,
  adminSectionLabelProps,
  adminSidebarColors,
  adminSidebarLogoProps,
  adminSidebarPopoverProps,
  adminSidebarShellProps,
} from "../../constants/adminSidebarTheme";
import { studentSurfaces } from "../../constants/studentThemeSurfaces";
import { useAdminTheme } from "../../context/AdminThemeContext";
import { hasFetchedOnce } from "../../utils/fetchFreshness";

const ANNOUNCEMENTS_SESSION_KEY = "announcementsShownThisSession";

/**
 * Student area: desktop fixed sidebar (md+); mobile compact top bar + full-width content below.
 */
const StudentPortalShell = ({ children }) => {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const userData = useSelector((state) => state.user.userData);
  const announcements = useSelector((state) => state.studentAnnouncements.announcements);
  const announcementsFetchedAt = useSelector(
    (state) => state.studentAnnouncements.announcementsFetchedAt
  );
  const performanceData = useSelector((state) => state.performance.analytics);
  const analyticsFetchedAt = useSelector(
    (state) => state.performance.analyticsFetchedAt
  );
  const perfLoading = useSelector((state) => state.performance.isLoading);
  const allExams = useSelector((state) => state.chapterExams.allExams) || [];
  const allHomeworks = useSelector((state) => state.chapterExams.allHomeworks) || [];
  const examsLoading = useSelector((state) => state.chapterExams.isLoading);
  const { themeMode } = useAdminTheme();
  const isDark = themeMode === "dark";
  const footerControls = adminFooterControlProps(isDark);
  const popoverChrome = adminSidebarPopoverProps(isDark);
  const {
    isOpen: isAnnouncementsOpen,
    onOpen: onAnnouncementsOpen,
    onClose: onAnnouncementsClose,
  } = useDisclosure();
  const {
    isOpen: isMobileNavOpen,
    onOpen: onMobileNavOpen,
    onClose: onMobileNavClose,
  } = useDisclosure();
  const autoShownRef = useRef(false);
  const mobileNavBtnRef = useRef(null);

  useEffect(() => {
    if (hasFetchedOnce(announcementsFetchedAt)) return;
    dispatch(getActiveAnnouncementsRequest());
  }, [dispatch, announcementsFetchedAt]);

  useEffect(() => {
    if (!userData?.id || hasFetchedOnce(analyticsFetchedAt)) return;
    dispatch(getPerformanceAnalyticsRequest(userData.id));
  }, [dispatch, userData?.id, analyticsFetchedAt]);

  useEffect(() => {
    if (allExams.length > 0 || allHomeworks.length > 0) return;
    dispatch(getAllExamsAndHomeworksRequest());
  }, [dispatch, allExams.length, allHomeworks.length]);

  useEffect(() => {
    if (autoShownRef.current || !announcements?.length) return;
    if (sessionStorage.getItem(ANNOUNCEMENTS_SESSION_KEY)) return;
    autoShownRef.current = true;
    onAnnouncementsOpen();
  }, [announcements, onAnnouncementsOpen]);

  useEffect(() => {
    onMobileNavClose();
  }, [pathname, search, onMobileNavClose]);

  const handleCloseAnnouncements = () => {
    sessionStorage.setItem(ANNOUNCEMENTS_SESSION_KEY, "true");
    onAnnouncementsClose();
  };

  const hasAnnouncements = announcements?.length > 0;

  const recentBellProps = {
    performanceData,
    allExams,
    allHomeworks,
    isLoading: perfLoading || examsLoading,
    isDark,
  };

  const AnnouncementsButton = ({ size = "sm", ...buttonProps }) =>
    hasAnnouncements ? (
      <Box position="relative">
        <IconButton
          aria-label="View announcements"
          icon={<HiSpeakerphone size={size === "sm" ? 18 : 20} />}
          variant="outline"
          borderColor={isDark ? brandPalette.cream : brandPalette.primary}
          color={brandPalette.primary}
          size={size}
          onClick={onAnnouncementsOpen}
          _hover={{
            bg: isDark ? "rgba(255,255,255,0.12)" : "rgba(91, 13, 22, 0.08)",
          }}
          {...buttonProps}
        />
        <Badge
          position="absolute"
          top="-6px"
          right="-6px"
          borderRadius="full"
          bg="red.500"
          color="white"
          fontSize="9px"
          minW="16px"
          h="16px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {announcements.length}
        </Badge>
      </Box>
    ) : null;

  const isHome = pathname === "/students" || pathname === "/home";
  const isCourses = pathname.startsWith("/students/courses");
  const isOfficeHours = pathname.startsWith("/students/office-hours");
  const isPerformanceAnalytics = pathname.startsWith("/students/performance-analytics");
  const isStudentExams =
    pathname.startsWith("/students/exams") || pathname === "/student/exams";
  const isQuickLinks = pathname.startsWith("/students/quick-links");
  const isQuickLinksHomework = isQuickLinks && !search.includes("tab=exams");
  const isQuickLinksExams = isQuickLinks && search.includes("tab=exams");

  const handleLogout = () => {
    dispatch(logoutUserAction());
  };

  const accountMenuListProps = {
    bg: "var(--surface-raised)",
    borderWidth: "1px",
    borderColor: popoverChrome.borderColor,
    borderRadius: "md",
    py: 1,
    minW: "10rem",
    boxShadow: "lg",
    zIndex: 50,
  };

  const accountMenuItemProps = {
    bg: "transparent",
    color: isDark ? brandPalette.cream : adminSidebarColors.inactiveText,
    fontWeight: "600",
    fontSize: "sm",
    py: 2.5,
    px: 3,
    iconSpacing: 3,
    _hover: {
      bg: isDark ? brandPalette.secondary : "rgba(91, 13, 22, 0.08)",
      color: isDark ? "white" : brandPalette.primary,
    },
    _focus: {
      bg: isDark ? brandPalette.secondary : "rgba(91, 13, 22, 0.08)",
      color: isDark ? "white" : brandPalette.primary,
    },
    _active: {
      bg: isDark ? brandPalette.secondary : "rgba(91, 13, 22, 0.08)",
      color: isDark ? "white" : brandPalette.primary,
    },
  };

  const SidebarNav = (
    <>
      <Box px={6} py={8}>
        <Image
          {...adminSidebarLogoProps(isDark)}
          onClick={() => window.location.replace("/students")}
        />
        <Text {...adminHeaderSubtitleProps(isDark)} mt={4} textAlign="center">
          Academic Portal
        </Text>
      </Box>

      <VStack as="nav" align="stretch" spacing={1} px={4} flex="1" pt={2}>
        <Link to="/students" style={{ textDecoration: "none" }}>
          <Flex {...adminNavItemProps(isHome, isDark)}>
            <Box as={RiDashboardLine} fontSize="18px" />
            <Text flex="1" textAlign="left">
              Home
            </Text>
          </Flex>
        </Link>
        <Link to="/students/courses" style={{ textDecoration: "none" }}>
          <Flex {...adminNavItemProps(isCourses, isDark)}>
            <Box as={FaBook} fontSize="18px" />
            <Text flex="1" textAlign="left">
              Chapters
            </Text>
          </Flex>
        </Link>
        <Link to="/students/office-hours" style={{ textDecoration: "none" }}>
          <Flex {...adminNavItemProps(isOfficeHours, isDark)}>
            <Box as={MdSchedule} fontSize="18px" />
            <Text flex="1" textAlign="left">
              One to one support
            </Text>
          </Flex>
        </Link>
        <Link to="/students/performance-analytics" style={{ textDecoration: "none" }}>
          <Flex {...adminNavItemProps(isPerformanceAnalytics, isDark)}>
            <Box as={FaChartLine} fontSize="18px" />
            <Text flex="1" textAlign="left">
              Performance Analytics
            </Text>
          </Flex>
        </Link>

        <Text {...adminSectionLabelProps(isDark)} mt={4} px={2}>
          Quick Links
        </Text>
        <Link to="/students/quick-links?tab=homework" style={{ textDecoration: "none" }}>
          <Flex {...adminNavItemProps(isQuickLinksHomework, isDark)}>
            <Box as={MdHomeWork} fontSize="18px" />
            <Text flex="1" textAlign="left">
              Homework
            </Text>
          </Flex>
        </Link>
        <Link to="/students/quick-links?tab=exams" style={{ textDecoration: "none" }}>
          <Flex {...adminNavItemProps(isQuickLinksExams, isDark)}>
            <Box as={MdQuiz} fontSize="18px" />
            <Text flex="1" textAlign="left">
              Exams
            </Text>
          </Flex>
        </Link>

        <Link to="/students/exams" style={{ textDecoration: "none" }}>
          <Flex {...adminNavItemProps(isStudentExams, isDark)}>
            <Box as={MdQuiz} fontSize="18px" />
            <Text flex="1" textAlign="left">
              Test yourself
            </Text>
          </Flex>
        </Link>
      </VStack>

      <Flex {...adminFooterProps(isDark)} align="center" gap={3} justify="space-between">
        

        <ThemeToggleButton {...footerControls.themeToggle} />
        <RecentContentBell {...recentBellProps} />
        <AnnouncementsButton />
        <Menu placement="top-end">
          <MenuButton
            as={IconButton}
            aria-label="Account and logout"
            icon={<RiSettings4Line size={22} />}
            {...adminFooterSettingsButtonProps(isDark)}
          />
          <MenuList {...accountMenuListProps}>
            <MenuItem
              {...accountMenuItemProps}
              icon={<RiLogoutBoxRLine size={18} />}
              onClick={handleLogout}
            >
              Log out
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>
    </>
  );

  return (
    <StudentWrapper roles={["Student"]}>
      <Box minH="100vh" bg={studentSurfaces.canvas} dir="ltr" position="relative" w="100%" overflowX="hidden">
        {/* Desktop: fixed sidebar (out of document flow for width) */}
        <Flex
          display={{ base: "none", md: "flex" }}
          position="fixed"
          left={0}
          top={0}
          zIndex={30}
          w="260px"
          h="100vh"
          {...adminSidebarShellProps(isDark)}
          direction="column"
        >
          {SidebarNav}
        </Flex>

        {/* Mobile: burger + logo + utility icons */}
        <Flex
          display={{ base: "flex", md: "none" }}
          position="sticky"
          top={0}
          zIndex={40}
          w="100%"
          maxW="100vw"
          bg="var(--surface-raised)"
          px={3}
          py={2.5}
          align="center"
          justify="space-between"
          gap={2}
          borderBottomWidth="1px"
          borderColor="var(--border-subtle)"
          flexShrink={0}
          boxShadow="0 2px 12px rgba(91, 13, 22, 0.06)"
        >
          <Flex align="center" gap={2} minW={0} flex="1">
            <IconButton
              ref={mobileNavBtnRef}
              aria-label="Open navigation menu"
              icon={<AiOutlineMenu size={20} />}
              onClick={onMobileNavOpen}
              size="sm"
              flexShrink={0}
              borderRadius="full"
              bg={brandPalette.primary}
              color={brandPalette.cream}
              _hover={{ bg: brandPalette.secondary }}
              _active={{ bg: brandPalette.secondary }}
            />
            <Text
              fontWeight="800"
              color={isDark ? brandPalette.cream : brandPalette.primary}
              fontSize="sm"
              noOfLines={1}
            >
              Edutopia
            </Text>
          </Flex>
          <Flex align="center" gap={1.5} flexShrink={0}>
            <ThemeToggleButton tone="onSurface" size="sm" iconSize={18} />
            <RecentContentBell {...recentBellProps} size="sm" flexShrink={0} placement="bottom-end" />
            <AnnouncementsButton size="sm" flexShrink={0} />
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label="Settings"
                icon={<RiSettings4Line />}
                variant="ghost"
                color={isDark ? brandPalette.cream : brandPalette.primary}
                size="sm"
                flexShrink={0}
                borderRadius="12px"
                _hover={{ bg: isDark ? "rgba(255,255,255,0.12)" : "rgba(91, 13, 22, 0.08)" }}
              />
              <MenuList {...accountMenuListProps}>
                <MenuItem
                  {...accountMenuItemProps}
                  icon={<RiLogoutBoxRLine size={18} />}
                  onClick={handleLogout}
                >
                  Log out
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        </Flex>

        <Drawer
          isOpen={isMobileNavOpen}
          placement="left"
          onClose={onMobileNavClose}
          finalFocusRef={mobileNavBtnRef}
          closeOnOverlayClick
          closeOnEsc
          size="xs"
        >
          <DrawerOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
          <DrawerContent {...adminMobileDrawerShellProps(isDark)}>
            <DrawerHeader {...adminMobileDrawerHeaderProps(isDark)}>
              <Text {...adminMobileDrawerTitleProps(isDark)}>Menu</Text>
              <IconButton
                aria-label="Close menu"
                icon={<RiCloseLine size={24} />}
                onClick={onMobileNavClose}
                {...adminMobileDrawerCloseButtonProps(isDark)}
              />
            </DrawerHeader>
            <DrawerBody p={0} overflowY="auto" display="flex" flexDirection="column">
              <VStack as="nav" align="stretch" spacing={1} px={4} py={4} flex="1">
                <Link to="/students" style={{ textDecoration: "none" }} onClick={onMobileNavClose}>
                  <Flex {...adminNavItemProps(isHome, isDark)}>
                    <Box as={RiDashboardLine} fontSize="18px" />
                    <Text flex="1" textAlign="left">Home</Text>
                  </Flex>
                </Link>
                <Link to="/students/courses" style={{ textDecoration: "none" }} onClick={onMobileNavClose}>
                  <Flex {...adminNavItemProps(isCourses, isDark)}>
                    <Box as={FaBook} fontSize="18px" />
                    <Text flex="1" textAlign="left">Chapters</Text>
                  </Flex>
                </Link>
                <Link to="/students/office-hours" style={{ textDecoration: "none" }} onClick={onMobileNavClose}>
                  <Flex {...adminNavItemProps(isOfficeHours, isDark)}>
                    <Box as={MdSchedule} fontSize="18px" />
                    <Text flex="1" textAlign="left">One to one support</Text>
                  </Flex>
                </Link>
                <Link to="/students/performance-analytics" style={{ textDecoration: "none" }} onClick={onMobileNavClose}>
                  <Flex {...adminNavItemProps(isPerformanceAnalytics, isDark)}>
                    <Box as={FaChartLine} fontSize="18px" />
                    <Text flex="1" textAlign="left">Performance Analytics</Text>
                  </Flex>
                </Link>

                <Text {...adminSectionLabelProps(isDark)} mt={4} px={2}>
                  Quick Links
                </Text>
                <Link to="/students/quick-links?tab=homework" style={{ textDecoration: "none" }} onClick={onMobileNavClose}>
                  <Flex {...adminNavItemProps(isQuickLinksHomework, isDark)}>
                    <Box as={MdHomeWork} fontSize="18px" />
                    <Text flex="1" textAlign="left">Homework</Text>
                  </Flex>
                </Link>
                <Link to="/students/quick-links?tab=exams" style={{ textDecoration: "none" }} onClick={onMobileNavClose}>
                  <Flex {...adminNavItemProps(isQuickLinksExams, isDark)}>
                    <Box as={MdQuiz} fontSize="18px" />
                    <Text flex="1" textAlign="left">Exams</Text>
                  </Flex>
                </Link>
                <Link to="/students/exams" style={{ textDecoration: "none" }} onClick={onMobileNavClose}>
                  <Flex {...adminNavItemProps(isStudentExams, isDark)}>
                    <Box as={MdQuiz} fontSize="18px" />
                    <Text flex="1" textAlign="left">Test yourself</Text>
                  </Flex>
                </Link>
              </VStack>
            </DrawerBody>
          </DrawerContent>
        </Drawer>

        {/* Main: offset for fixed sidebar; width must not be 100% + margin or content overflows */}
        <Box
          w={{ base: "100%", md: "calc(100% - 260px)" }}
          maxW={{ base: "100%", md: "calc(100% - 260px)" }}
          minW={0}
          ml={{ base: 0, md: "260px" }}
          minH="100vh"
        >
          {children}
        </Box>
      </Box>

      <AnnouncementsCarousel
        announcements={announcements}
        isOpen={isAnnouncementsOpen}
        onClose={handleCloseAnnouncements}
      />
    </StudentWrapper>
  );
};

export default StudentPortalShell;
